import { useEffect, useRef, useState } from "react";
import { NavLink, Link, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, LineChart, ShoppingCart, Phone, Trophy, CalendarDays, ChevronLeft, ChevronRight, ArrowLeftRight, Layers } from "lucide-react";
import { PullToRefresh } from "@/components/PullToRefresh";
import PromoBanner from "@/components/PromoBanner";
import MedalCelebration from "@/components/MedalCelebration";
import PostMatchReport from "@/components/PostMatchReport";
import WatchLiveButton from "@/components/WatchLiveButton";
import PlayerSearchBar from "@/components/PlayerSearchBar";
import DepositBoxEntry from "@/components/SafetyDepositBox";
import CartDrawer from "@/components/CartDrawer";
import FloatingAssistant from "@/components/FloatingAssistant";
import WelcomeCardModal from "@/components/WelcomeCardModal";
import GenderPrompt from "@/components/GenderPrompt";
import PotHypeBanner from "@/components/PotHypeBanner";
import Avatar from "@/components/Avatar";
import HeaderMenu from "@/components/HeaderMenu";
import ViewSwitcher from "@/components/ViewSwitcher";
import Translator from "@/components/Translator";
import { WalletChip } from "@/components/WalletChip";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import PhonePromptModal from "@/components/PhonePromptModal";
import { useContent } from "@/context/ContentContext";
import { canSeePage, pageForPath } from "@/lib/pages";
import MusicPlayer from "@/components/MusicPlayer";

const LDA_LOGO = "https://customer-assets.emergentagent.com/job_pickle-os/artifacts/kb84zhqr_Background%20Eraser.png";
const BPL_LOGO = "https://customer-assets.emergentagent.com/job_pickle-os/artifacts/cj28ut5a_AFEA36B2-2E75-4ED3-9048-93AFF9BF75C1.png";
const PB_LOGO = "/brand/pb-logo.png";
const BALL_IMG = "https://static.prod-images.emergentagent.com/jobs/ccb7baf7-4fe3-4351-b3c9-d8e5a28ac676/images/59d9943567bf36ae0b41ae749f81d54c0a77acfc2a89fea12009a2a1aea0f643.png";

const TABS = [
  { to: "/profile", label: "Player Profile", avatar: true, testid: "tab-profile" },
  { to: "/book", label: "Book a Session", img: BALL_IMG, imgClass: "animate-fly-ball", testid: "tab-book" },
  { to: "/training", label: "LDA Training Hub", img: LDA_LOGO, testid: "tab-training" },
  { to: "/league", label: "BPL League", img: BPL_LOGO, testid: "tab-league" },
  { to: "/community", label: "Community", img: PB_LOGO, testid: "tab-community" },
  { to: "/vault", label: "Card Vault", img: "/icons/vault.png", testid: "tab-vault" },
];
const TOURNEY_TAB = { to: "/tournaments", label: "Tournaments", img: "/icons/pbpa-tour-sm.png", testid: "tab-tournaments" };
const TD_TOURNEY_TAB = { to: "/tournaments", label: "TD Tournaments", img: "/icons/pbpa-tour-sm.png", testid: "tab-tournaments" };
const ADMIN_TAB = { to: "/admin", label: "Admin", icon: LineChart, testid: "tab-admin" };
const COACH_TAB = { to: "/coaches-corner", label: "Coaches Corner", icon: CalendarDays, testid: "tab-coaches-corner" };
const SCOREKEEPER_TAB = { to: "/scorekeeper", label: "Score Entry", icon: LineChart, testid: "tab-scorekeeper" };
const STORE_TAB = { to: "/store", label: "Pro Shop", img: "/icons/proshop-sm.png", imgClass: "scale-125", testid: "tab-store" };

export default function Layout() {
  const { user, realUser, logout, activeView, canSwitchView, preview } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const contentCtx = useContent();
  const access = (path) => {
    const p = pageForPath(path);
    return !p || canSeePage(user, contentCtx?.content?.[`access.${p.key}`]);
  };

  const viewLabel = activeView === "td" ? "Director View" : activeView === "coach" ? "Coach View" : "Player View";
  const navRef = useRef(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    let sx = 0, sy = 0, st = 0, valid = false;
    const blocked = (el) => {
      for (let n = el; n && n !== document.body; n = n.parentElement) {
        if (["INPUT", "TEXTAREA"].includes(n.tagName)) return true;
        const s = getComputedStyle(n);
        if (n.scrollWidth > n.clientWidth + 5 && (s.overflowX === "auto" || s.overflowX === "scroll")) return true;
      }
      return false;
    };
    const onStart = (e) => {
      if (e.touches.length !== 1) { valid = false; return; }
      const t = e.touches[0];
      sx = t.clientX; sy = t.clientY; st = Date.now();
      valid = !blocked(e.target);
    };
    const onEnd = (e) => {
      if (!valid) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - sx, dy = t.clientY - sy;
      if (Date.now() - st < 600 && dx > 90 && Math.abs(dy) < 70 && dx > Math.abs(dy) * 2) navigate(-1);
    };
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchend", onEnd, { passive: true });
    return () => { document.removeEventListener("touchstart", onStart); document.removeEventListener("touchend", onEnd); };
  }, [navigate]);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      // collapsing header is a phone feature — on desktop it fights the scrollbar and stutters
      if (window.innerWidth >= 640) { setCollapsed(false); lastY = window.scrollY; return; }
      const y = window.scrollY;
      if (y < 64) setCollapsed(false);
      else if (y > lastY + 6) setCollapsed(true);
      else if (y < lastY - 6) setCollapsed(false);
      lastY = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const handleLogout = () => { logout(); navigate("/auth"); };

  const allTabs = user?.role === "scorekeeper"
    ? [SCOREKEEPER_TAB, TABS[0]]
    : user?.role === "admin"
    ? [...TABS, TOURNEY_TAB, SCOREKEEPER_TAB, STORE_TAB, COACH_TAB, ADMIN_TAB]
    : user?.is_td
      ? [TD_TOURNEY_TAB, { to: "/league", label: "BPL League", img: BPL_LOGO, testid: "tab-league" }, STORE_TAB, ADMIN_TAB]
      : user?.is_coach
        ? [COACH_TAB, TABS[1], STORE_TAB, ADMIN_TAB]
        : [...TABS, TOURNEY_TAB, STORE_TAB];
  const tabs = allTabs.filter((t) => access(t.to));

  let navOrder = [];
  try { navOrder = JSON.parse(contentCtx?.content?.["nav.order"] || "[]"); } catch { navOrder = []; }
  const orderedTabs = [...tabs].sort((a, b) => {
    const ia = navOrder.indexOf(a.to), ib = navOrder.indexOf(b.to);
    return (ia === -1 ? 1000 + tabs.indexOf(a) : ia) - (ib === -1 ? 1000 + tabs.indexOf(b) : ib);
  });
  const moveTab = (path, dir) => {
    const cur = orderedTabs.map((t) => t.to);
    const i = cur.indexOf(path), j = i + dir;
    if (j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    contentCtx.save("nav.order", JSON.stringify(cur), { quiet: true });
  };

  if (!access(location.pathname)) {
    const blocked = pageForPath(location.pathname);
    const aud = contentCtx?.content?.[`access.${blocked?.key}`];
    if (aud === "paid" && user && (user.membership || "free") === "free")
      return <Navigate to={`/upgrade?reason=${encodeURIComponent(`${blocked.label} is available to paid members only.`)}`} replace />;
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="min-h-screen bg-pb-base text-white">
      <PullToRefresh />
      {realUser?.must_change_password && <ChangePasswordModal forced />}
      {realUser && !realUser.must_change_password && !preview && <PhonePromptModal />}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-md border-b border-pb-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-y-1 py-1.5 min-h-14 sm:h-16 sm:py-0 sm:flex-nowrap">
            <div className="flex items-center gap-2 sm:gap-3">
              <img src="/brand/pb-logo.png"
                alt="Pickle Bullpen"
                className="h-10 w-auto sm:h-12 object-contain drop-shadow-[0_0_10px_rgba(163,230,53,0.35)]"
                data-testid="header-logo" />
              <div className={`leading-none ${collapsed ? "hidden sm:block" : ""}`}>
                <div className="font-display font-black text-base sm:text-xl uppercase tracking-tight">Pickle Bullpen</div>
                {canSwitchView ? (
                  <div data-testid="view-switch-label"
                    className={`text-[9px] uppercase tracking-[0.3em] flex items-center gap-1 ${activeView === "td" ? "text-pb-red" : activeView === "coach" ? "text-amber-400" : "text-pb-cyan"}`}>
                    <ArrowLeftRight className="w-2.5 h-2.5" /> {viewLabel}
                  </div>
                ) : (
                  <div className="text-[9px] uppercase tracking-[0.3em] text-pb-red">Performance OS</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4">
              <WatchLiveButton compact={collapsed} className="hidden xs:inline-flex sm:inline-flex" />
              <div className={`${collapsed ? "hidden" : "flex"} items-center`}>
                <ViewSwitcher />
              </div>
              <HeaderMenu showFaq={access("/faq")} />
            </div>
          </div>
          <div className={`flex items-center gap-3 overflow-hidden transition-all duration-300 ${collapsed ? "max-h-0 opacity-0 pb-0 pt-0" : "max-h-14 opacity-100 pb-2 pt-0.5"}`} data-testid="header-icon-row">
            <WalletChip />
            <MusicPlayer />
          </div>
        </div>
        <div className={`overflow-hidden transition-all duration-300 ${collapsed ? "max-h-0 opacity-0" : "max-h-16 opacity-100"}`}>
        <nav ref={navRef} className="max-w-7xl mx-auto px-2 md:px-8 flex overflow-x-auto nav-scroll">
          {orderedTabs.map((t) => (
            <div key={t.to} className="flex items-center shrink-0">
            <NavLink to={t.to} data-testid={t.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-3 text-xs md:text-sm font-bold uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                  isActive ? "border-pb-red text-white" : "border-transparent text-pb-border-foreground text-zinc-500 hover:text-zinc-300"
                }`
              }>
              {t.avatar ? (
                <Avatar src={user?.avatar} name={user?.name} size={22} testId="tab-profile-avatar" />
              ) : t.img ? (
                <img src={t.img} alt={t.label} className={`w-6 h-6 object-contain shrink-0 ${t.imgClass || ""}`} />
              ) : (
                <t.icon className="w-4 h-4" />
              )}
              {t.label}
            </NavLink>
            {contentCtx?.editMode && (
              <span className="flex flex-col mr-2 -ml-1">
                <button data-testid={`tab-move-left-${t.testid}`} onClick={() => moveTab(t.to, -1)} title="Move tab left"
                  className="w-5 h-4 flex items-center justify-center text-pb-cyan hover:bg-pb-cyan hover:text-black border border-pb-cyan/50 border-b-0">
                  <ChevronLeft className="w-3 h-3" />
                </button>
                <button data-testid={`tab-move-right-${t.testid}`} onClick={() => moveTab(t.to, 1)} title="Move tab right"
                  className="w-5 h-4 flex items-center justify-center text-pb-cyan hover:bg-pb-cyan hover:text-black border border-pb-cyan/50">
                  <ChevronRight className="w-3 h-3" />
                </button>
              </span>
            )}
            </div>
          ))}
        </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="relative z-10"><PromoBanner /></div>
        <PotHypeBanner />
        <div className="flex items-start justify-end gap-2">
          <div className="flex-1 min-w-0"><PlayerSearchBar /></div>
          {["/vault", "/profile"].includes(location.pathname) && <DepositBoxEntry />}
        </div>
        <Outlet />
        <Translator />
      </main>
      <CartDrawer />
      <MedalCelebration />
      <PostMatchReport />
      <FloatingAssistant />
      <WelcomeCardModal />
      <GenderPrompt />
      <footer className="relative z-10 border-t border-pb-border mt-8 bg-pb-base">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 text-center space-y-2">
          <a href="tel:9418995300" data-testid="footer-contact"
            className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-pb-red transition-colors font-semibold">
            <Phone className="w-4 h-4 text-pb-red" /> Contact Us: 941-899-5300
          </a>
          <p className="text-[11px] text-zinc-500">Text us for questions — fastest response.</p>
          <p className="text-[11px] text-zinc-500 tracking-wide" data-testid="footer-ownership">
            Owned and operated by: The Pickle Bullpin LLC.
          </p>
          <p className="text-[11px] tracking-widest uppercase flex flex-wrap justify-center gap-x-4 gap-y-1" data-testid="footer-legal">
            <Link to="/privacy-policy" data-testid="footer-privacy" className="text-zinc-500 hover:text-pb-red">Privacy Policy</Link>
            <Link to="/terms-of-service" data-testid="footer-terms" className="text-zinc-500 hover:text-pb-red">Terms of Service</Link>
            <Link to="/return-policy" data-testid="footer-returns" className="text-zinc-500 hover:text-pb-red">Return Policy</Link>
          </p>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="inline-flex items-center gap-2 mt-2 px-8 py-3 border border-pb-border text-zinc-300 hover:border-pb-red hover:text-pb-red font-black uppercase tracking-widest text-xs transition-colors">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </footer>
    </div>
  );
}
